import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { renderRouteArt, getAqiColor } from '@/utils/routeArt';
import { calculateEnvironmentalSummary } from '@/utils/calculations/difficultyScore';
import type { RunningActivity, EnvironmentalSummary } from '@/types/running';

interface ActivityDetailProps {
  activity: RunningActivity | null;
  isOpen: boolean;
  onClose: () => void;
}

export function ActivityDetail({ activity, isOpen, onClose }: ActivityDetailProps) {
  const [routeArtUrl, setRouteArtUrl] = useState<string | null>(null);
  const [envSummary, setEnvSummary] = useState<EnvironmentalSummary | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [userPhoto, setUserPhoto] = useState<string | null>(null);
  const [sharePreview, setSharePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!activity || !isOpen) return;

    // Render route art
    if (activity.route_polyline && activity.route_polyline.length >= 2) {
      const canvas = renderRouteArt(activity.route_polyline, 600, 300);
      setRouteArtUrl(canvas.toDataURL('image/png'));
    } else {
      setRouteArtUrl(null);
    }

    // Calculate environmental summary from AQI data
    if (activity.aqi_data?.aqi_samples?.length) {
      const summary = calculateEnvironmentalSummary({
        aqi_samples: activity.aqi_data.aqi_samples,
        elevation_gain: activity.elevation_gain_meters,
        distance_meters: activity.distance_meters,
      });
      setEnvSummary(summary);
    } else {
      setEnvSummary(null);
    }

    setUserPhoto(null);
    setSharePreview(null);
  }, [activity, isOpen]);

  // Regenerate preview when photo changes
  useEffect(() => {
    if (!activity || !isOpen) return;
    generateSharePreview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPhoto]);

  const distKm = activity ? activity.distance_meters / 1000 : 0;
  const paceMin = activity ? Math.floor(activity.pace_seconds_per_km / 60) : 0;
  const paceSec = activity ? Math.floor(activity.pace_seconds_per_km % 60) : 0;

  const formatDuration = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    return `${m}m ${sec}s`;
  };

  const formatDurationShort = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m ${sec.toString().padStart(2, '0')}s`;
    return `${m}m ${sec.toString().padStart(2, '0')}s`;
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setUserPhoto(reader.result as string);
    reader.readAsDataURL(file);
  };

  /** Build the shareable canvas (1080x1920 for Instagram stories) */
  const buildShareCanvas = useCallback(async (): Promise<HTMLCanvasElement> => {
    const W = 1080;
    const H = 1920;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d')!;

    // Background
    if (userPhoto) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = userPhoto;
      });
      const imgRatio = img.width / img.height;
      const canvasRatio = W / H;
      let sx = 0, sy = 0, sw = img.width, sh = img.height;
      if (imgRatio > canvasRatio) { sw = img.height * canvasRatio; sx = (img.width - sw) / 2; }
      else { sh = img.width / canvasRatio; sy = (img.height - sh) / 2; }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
      ctx.fillRect(0, 0, W, H);
    } else {
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, '#0d1117');
      grad.addColorStop(0.5, '#111827');
      grad.addColorStop(1, '#0d1117');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
    }

    // Route art (AQI-colored)
    if (activity && activity.route_polyline && activity.route_polyline.length >= 2) {
      const routeCanvas = renderRouteArt(activity.route_polyline, W, 700, {
        backgroundColor: null,
        strokeColor: userPhoto ? '#ff6b35' : '#10b981',
        glowColor: userPhoto ? '#ff9a5c' : '#06b6d4',
        padding: 80,
        lineWidth: 6,
        glowWidth: 16,
      });
      ctx.drawImage(routeCanvas, 0, 200);
    }

    // Stats
    const statsY = 960;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(0, statsY - 40, W, 560);

    // Title
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 36px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(activity?.title || 'Activity', 60, statsY + 20);

    // Date
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.font = '24px system-ui, -apple-system, sans-serif';
    ctx.fillText(
      activity ? new Date(activity.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }) : '',
      60, statsY + 60
    );

    if (distKm > 0.01) {
      ctx.textAlign = 'center';
      ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.fillText('Distance', W / 2, statsY + 130);
      ctx.font = 'bold 80px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(`${distKm.toFixed(2)} km`, W / 2, statsY + 210);

      const leftX = W / 3;
      const rightX = (W * 2) / 3;
      ctx.font = 'bold 26px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.fillText('Pace', leftX, statsY + 270);
      ctx.font = 'bold 52px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(
        activity?.pace_seconds_per_km ? `${paceMin}:${paceSec.toString().padStart(2, '0')} /km` : '--',
        leftX, statsY + 330
      );
      ctx.font = 'bold 26px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.fillText('Time', rightX, statsY + 270);
      ctx.font = 'bold 52px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(formatDurationShort(activity?.duration_seconds || 0), rightX, statsY + 330);
    } else {
      ctx.textAlign = 'center';
      ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.fillText('Time', W / 2, statsY + 150);
      ctx.font = 'bold 80px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(formatDurationShort(activity?.duration_seconds || 0), W / 2, statsY + 240);
    }

    // Environmental data
    if (envSummary) {
      const envY = statsY + 400;
      ctx.textAlign = 'center';
      const envItems: { label: string; value: string; color: string }[] = [];
      if (envSummary.avg_temperature !== null) envItems.push({ label: 'Temp', value: `${envSummary.avg_temperature.toFixed(0)}\u00B0C`, color: '#22d3ee' });
      if (envSummary.avg_humidity !== null) envItems.push({ label: 'Humidity', value: `${envSummary.avg_humidity.toFixed(0)}%`, color: '#60a5fa' });
      if (activity?.aqi_data) {
        const aqiColor = activity.aqi_data.average_aqi <= 50 ? '#34d399' : activity.aqi_data.average_aqi <= 100 ? '#fbbf24' : '#f87171';
        envItems.push({ label: 'AQI', value: `${activity.aqi_data.average_aqi}`, color: aqiColor });
      }
      if (envSummary.sun_exposure_pct > 0) envItems.push({ label: 'Sun', value: `${envSummary.sun_exposure_pct}%`, color: '#fbbf24' });

      if (envItems.length > 0) {
        const itemWidth = W / envItems.length;
        envItems.forEach((item, i) => {
          const x = itemWidth * i + itemWidth / 2;
          ctx.font = 'bold 36px system-ui, -apple-system, sans-serif';
          ctx.fillStyle = item.color;
          ctx.fillText(item.value, x, envY);
          ctx.font = '20px system-ui, -apple-system, sans-serif';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
          ctx.fillText(item.label, x, envY + 30);
        });
      }

      if (envSummary.difficulty_score > 0) {
        const diffY = envY + 75;
        const diffColor = envSummary.difficulty_score <= 3 ? '#34d399' : envSummary.difficulty_score <= 6 ? '#fbbf24' : '#f87171';
        ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
        ctx.fillStyle = diffColor;
        ctx.textAlign = 'center';
        ctx.fillText(`Difficulty ${envSummary.difficulty_score.toFixed(1)}/10  \u2022  Harder than ${envSummary.difficulty_percentile}% globally`, W / 2, diffY);
      }
    }

    // Branding
    ctx.textAlign = 'center';
    ctx.font = 'bold 32px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = '#10b981';
    ctx.fillText('BLURP.IE', W / 2, H - 60);
    ctx.font = '18px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.fillText('blurpie.com', W / 2, H - 30);

    return canvas;
  }, [activity, userPhoto, envSummary, distKm, paceMin, paceSec]);

  const generateSharePreview = useCallback(async () => {
    if (!activity) return;
    const canvas = await buildShareCanvas();
    setSharePreview(canvas.toDataURL('image/jpeg', 0.9));
  }, [activity, buildShareCanvas]);

  const handleShare = useCallback(async () => {
    setIsSharing(true);
    try {
      const canvas = await buildShareCanvas();
      const blob = await new Promise<Blob>((resolve) =>
        canvas.toBlob((b) => resolve(b!), 'image/png')
      );
      const file = new File([blob], 'blurpie-activity.png', { type: 'image/png' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: activity?.title || 'Activity',
          text: `${activity?.title} — ${distKm > 0.01 ? `${distKm.toFixed(1)}km` : formatDuration(activity?.duration_seconds || 0)} on Blurp.ie`,
          files: [file],
        });
      } else {
        // Fallback: download
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `blurpie-${Date.now()}.png`;
        link.click();
        URL.revokeObjectURL(link.href);
      }
    } catch {
      // User cancelled
    } finally {
      setIsSharing(false);
    }
  }, [activity, buildShareCanvas, distKm]);

  if (!isOpen || !activity) return null;

  const isStationary = ['yoga', 'strength', 'treadmill', 'swim'].includes(activity.activity_type);
  const avgAqi = activity.aqi_data?.average_aqi;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[65] bg-[#0d1117] overflow-y-auto"
      >
        <div className="min-h-screen pb-8">
          {/* Close button */}
          <div className="absolute top-4 right-4 z-10">
            <button onClick={onClose} className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Share preview OR route art */}
          {sharePreview ? (
            <div className="relative">
              <img src={sharePreview} alt="Share preview" className="w-full" style={{ maxHeight: '50vh', objectFit: 'cover', objectPosition: 'top' }} />
              <div className="absolute bottom-2 right-2 bg-black/60 px-2 py-1 rounded text-xs text-white/70">
                Share preview
              </div>
            </div>
          ) : routeArtUrl ? (
            <img src={routeArtUrl} alt="Route" className="w-full aspect-[2/1] object-cover" />
          ) : (
            <div className="w-full aspect-[2/1] bg-[#161b22] flex items-center justify-center">
              <span className="text-gray-500 text-lg">
                {isStationary ? 'Indoor Activity' : 'No route data'}
              </span>
            </div>
          )}

          {/* Add photo for share */}
          <div className="px-6 pt-4">
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoSelect} className="hidden" />
            {!userPhoto ? (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-3 bg-white/5 hover:bg-white/10 border border-dashed border-white/20 rounded-xl text-gray-400 text-sm flex items-center justify-center gap-2 transition-colors mb-4"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Add Photo for Share
              </button>
            ) : (
              <button
                onClick={() => { setUserPhoto(null); setSharePreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                className="w-full py-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-xl text-red-400 text-sm flex items-center justify-center gap-2 transition-colors mb-4"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Remove Photo
              </button>
            )}
          </div>

          {/* Content */}
          <div className="px-6">
            {/* Title + difficulty */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1 mr-4">
                <h2 className="text-2xl font-bold text-white">{activity.title}</h2>
                <p className="text-gray-500 text-sm mt-1">
                  {new Date(activity.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
                {activity.description && <p className="text-gray-400 text-sm mt-2">{activity.description}</p>}
              </div>
              {envSummary && (
                <div className="flex flex-col items-center">
                  <div className="relative w-16 h-16">
                    <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r="15.5" fill="none" stroke="#1e293b" strokeWidth="3" />
                      <circle
                        cx="18" cy="18" r="15.5" fill="none"
                        stroke={envSummary.difficulty_score <= 3 ? '#10b981' : envSummary.difficulty_score <= 6 ? '#f59e0b' : '#ef4444'}
                        strokeWidth="3"
                        strokeDasharray={`${envSummary.difficulty_score * 9.74} 97.4`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">{envSummary.difficulty_score.toFixed(1)}</span>
                    </div>
                  </div>
                  <span className="text-gray-500 text-xs mt-1">difficulty</span>
                </div>
              )}
            </div>

            {/* Main stats */}
            {!isStationary && distKm > 0.01 ? (
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white/5 rounded-2xl p-4 text-center border border-white/5">
                  <div className="text-2xl font-bold text-white">{distKm.toFixed(2)}</div>
                  <div className="text-xs text-gray-400 mt-1">km</div>
                </div>
                <div className="bg-white/5 rounded-2xl p-4 text-center border border-white/5">
                  <div className="text-2xl font-bold text-white">
                    {activity.pace_seconds_per_km ? `${paceMin}:${paceSec.toString().padStart(2, '0')}` : '--'}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">/km</div>
                </div>
                <div className="bg-white/5 rounded-2xl p-4 text-center border border-white/5">
                  <div className="text-2xl font-bold text-white">{formatDuration(activity.duration_seconds)}</div>
                  <div className="text-xs text-gray-400 mt-1">time</div>
                </div>
              </div>
            ) : (
              <div className="bg-white/5 rounded-2xl p-6 text-center border border-white/5 mb-6">
                <div className="text-3xl font-bold text-white">{formatDuration(activity.duration_seconds)}</div>
                <div className="text-sm text-gray-400 mt-1">Total Time</div>
              </div>
            )}

            {/* Environmental conditions */}
            {(envSummary || avgAqi) && (
              <div className="bg-white/5 rounded-2xl p-4 border border-white/5 mb-4">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Conditions</h3>
                <div className="grid grid-cols-4 gap-3">
                  {envSummary?.avg_temperature !== null && envSummary?.avg_temperature !== undefined && (
                    <div className="text-center">
                      <div className="text-lg font-bold text-cyan-400">{envSummary.avg_temperature.toFixed(0)}&deg;</div>
                      <div className="text-xs text-gray-500">temp</div>
                    </div>
                  )}
                  {envSummary?.avg_humidity !== null && envSummary?.avg_humidity !== undefined && (
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-400">{envSummary.avg_humidity.toFixed(0)}%</div>
                      <div className="text-xs text-gray-500">humidity</div>
                    </div>
                  )}
                  {avgAqi !== undefined && (
                    <div className="text-center">
                      <div className="text-lg font-bold" style={{ color: getAqiColor(avgAqi) }}>{avgAqi}</div>
                      <div className="text-xs text-gray-500">AQI</div>
                    </div>
                  )}
                  {envSummary && envSummary.sun_exposure_pct > 0 && (
                    <div className="text-center">
                      <div className="text-lg font-bold text-amber-400">{envSummary.sun_exposure_pct}%</div>
                      <div className="text-xs text-gray-500">sun</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Difficulty banner */}
            {envSummary && envSummary.difficulty_score > 0 && (
              <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-2xl p-4 border border-emerald-500/20 mb-6">
                <div className="text-white font-semibold mb-1">
                  Harder than {envSummary.difficulty_percentile}% of activities globally
                </div>
                {envSummary.difficulty_factors.length > 0 && (
                  <div className="text-sm text-gray-400">
                    Factors: {envSummary.difficulty_factors.map(f => f.label).join(', ')}
                  </div>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleShare}
                disabled={isSharing}
                className="flex-1 py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 rounded-xl font-bold text-black transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                {isSharing ? 'Sharing...' : 'Share to Social Media'}
              </button>
            </div>

            <button
              onClick={onClose}
              className="block w-full py-3 text-center text-sm text-gray-500 hover:text-white transition-colors mt-3"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
